import { useEffect } from 'react';
import { Button, Group, Loader, SimpleGrid, Stack } from '@mantine/core';
import { IconHelp } from '@tabler/icons-react';
import { useIsAuthenticated } from '@azure/msal-react';
import { hasMagicLinkSession } from '../helpers/TokenRefresh';
import { useUserContext } from '../providers/UserContext';
import { MyTrainingsSection } from '../components/home/MyTrainingsSection';
import { MyRentalsSection } from '../components/home/MyRentalsSection';
import { CalendarSection } from '../components/home/CalendarSection';
import { TeamFilterBadges } from '../components/home/FilterBadges';
import { TopCardsCard, TopOwnGoalsCard, TopScorersCard } from '../components/home/LeaderboardCards';
import { MessagesSection } from '../components/home/MessagesSection';
import { useSeasonLeaders, useTeamFilter, useTrainings } from '../components/home/useHomeData';
import { startDashboardTour, startDashboardTourOnFirstVisit } from '../helpers/dashboardTour';

function Home() {
  const isAuthenticated = useIsAuthenticated() || hasMagicLinkSession();
  if (!isAuthenticated) return null;
  return <MemberDashboard />;
}

// Each area of the dashboard is a self-contained component in components/home/.
// Data several areas share is loaded once here and passed down:
//   - trainings: "Meine Trainings" lists + calendar
//   - teamFilter: calendar + leaderboards (picking a team scopes both)
//   - leaders: the three leaderboard cards (one request for all three)
// To rearrange the page, just reorder the sections below.
function MemberDashboard() {
  const { currentPerson } = useUserContext();
  const { trainings, loading } = useTrainings(currentPerson);
  const teamFilter = useTeamFilter();
  const leaders = useSeasonLeaders(teamFilter.selectedTeams);

  // Start the tutorial once the sections have rendered (anchors must exist).
  useEffect(() => {
    if (!loading) startDashboardTourOnFirstVisit();
  }, [loading]);

  if (loading) return <Loader size="md" mt="md" />;

  return (
    <Stack gap="lg" mt="md">
      <Group justify="flex-end" mb={-16}>
        <Button
          data-tour="dashboard-help"
          variant="subtle"
          size="xs"
          leftSection={<IconHelp size={14} />}
          onClick={startDashboardTour}
        >
          Anleitung
        </Button>
      </Group>

      <MessagesSection />

      <CalendarSection trainings={trainings} teamFilter={teamFilter} />

      <MyTrainingsSection trainings={trainings} />

      <MyRentalsSection />

      <TeamFilterBadges
        allTeams={teamFilter.allTeams}
        selectedTeams={teamFilter.selectedTeams}
        onToggle={teamFilter.toggleTeam}
      />
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" data-tour="dashboard-leaders">
        <TopScorersCard scorers={leaders.topScorers} />
        <TopCardsCard cards={leaders.topCards} />
        <TopOwnGoalsCard ownGoals={leaders.topOwnGoals} />
      </SimpleGrid>
    </Stack>
  );
}

export default Home;
